// Оживлённая картинка пустого состояния.
//
// ## Откуда она и почему такая
//
// Решение владельца 02.09.2026: оживлённые картинки берём из AlignUI и ставим на всех трёх
// поверхностях. В доноре чатов их нет вовсе — там обычные неподвижные SVG, — а в AlignUI лежит
// 3800 значков Lottie. Отсюда и проигрыватель, и формат.
//
// ## Три правила, из которых она собрана
//
// **Проигрывается ОДИН раз и замирает.** Вечное движение на пустом экране — это мигающая
// картинка, мимо которой человек читает текст, и разряженная батарея на телефоне. Здесь оно
// нужно только затем, чтобы экран не выглядел мёртвым в первую секунду.
//
// **Проигрыватель приезжает ОТДЕЛЬНО от программы.** Он весит 70 килобайт в сжатом виде — это
// шестая часть всей нашей сборки, и класть его в общий кусок ради картинки на пустом экране
// нельзя: платить за него будут все, а видеть его будет один человек из десяти. Поэтому и
// проигрыватель, и сама картинка подгружаются в тот момент, когда пустое состояние правда
// показалось.
//
// **Цвет берётся из темы, а не из файла.** В файлах AlignUI обводка чёрная; на тёмной теме
// чёрное по тёмному не видно вовсе. Цвет читается с самого узла (`currentColor`) и
// подставляется в разбор перед запуском — поэтому картинка следует теме, как и всё остальное.
//
// Пока проигрыватель едет и при системном «уменьшить движение» показывается та же картинка
// неподвижной: это не запасной вариант на случай поломки, а полноценное состояние.

import { useEffect, useRef, useState } from 'react';

import { Illustration, type ИмяКартинки } from './index';

/** Для каких картинок есть оживлённый вариант. Остальные показываются неподвижными. */
const ОЖИВЛЁННЫЕ = new Set<ИмяКартинки>(['деньги', 'заявки', 'спокойно', 'переписка', 'люди']);

export function естьОживление(имя: ИмяКартинки): boolean {
  return ОЖИВЛЁННЫЕ.has(имя);
}

/** Ключ, которым проверка глазами включает движение под управлением прогона. */
const СТВОРКА_ПРОГОНА = 'bark.motion.show-in-automation';

/** Разбор Lottie: нам важна только структура, которую мы перекрашиваем. */
type Разбор = Record<string, unknown>;

/**
 * Перекрасить ролик в цвета темы: тёмное — линией, цветное — акцентом.
 *
 * В роликах AlignUI два цвета: почти чёрная линия и зелёный акцент. Оба чужие. Чёрная линия на
 * тёмной теме не видна вовсе, а зелёный не встречается в продукте ни разу — зелёным у нас
 * ничего не обозначается, и человек прочитал бы его как значащий.
 *
 * Разделение по яркости, а не по списку цветов: у одного ролика акцент 35-173-41, у другого
 * 45-186-51, у третьего вдобавок красный технический — перечислять их значит держать список,
 * который отстанет на первой новой картинке.
 *
 * Цвет в Lottie лежит долями единицы и разбросан по дереву фигур, поэтому обход рекурсивный.
 */
function перекрасить(
  узел: unknown,
  линия: readonly [number, number, number],
  акцент: readonly [number, number, number],
): void {
  if (Array.isArray(узел)) {
    for (const элемент of узел) {
      перекрасить(элемент, линия, акцент);
    }
    return;
  }
  if (узел === null || typeof узел !== 'object') {
    return;
  }
  const запись = узел as Разбор;
  const тип = запись['ty'];
  if (тип === 'st' || тип === 'fl') {
    const краска = запись['c'] as { k?: unknown } | undefined;
    if (краска !== undefined && Array.isArray(краска.k) && краска.k.length >= 3) {
      const [r, g, b] = краска.k as number[];
      const яркость = (r ?? 0) + (g ?? 0) + (b ?? 0);
      const цвет = яркость < 0.3 ? линия : акцент;
      краска.k = [цвет[0], цвет[1], цвет[2], 1];
    }
  }
  for (const значение of Object.values(запись)) {
    перекрасить(значение, линия, акцент);
  }
}

/** «rgb(82, 88, 102)» → доли единицы. Ничего не разобрали — вернём null, и картинка останется как есть. */
function разобратьЦвет(строка: string): [number, number, number] | null {
  const числа = строка.match(/\d+(\.\d+)?/g);
  if (числа === null || числа.length < 3) {
    return null;
  }
  const [r, g, b] = числа.map((ч) => Number(ч) / 255);
  if (r === undefined || g === undefined || b === undefined) {
    return null;
  }
  return [r, g, b];
}

export function ОживлённаяКартинка({ имя }: { имя: ИмяКартинки }) {
  const узел = useRef<HTMLDivElement>(null);
  // Щуп акцента: цвет темы читается с настоящего узла, а не пишется числом в коде.
  const щупАкцента = useRef<HTMLSpanElement>(null);
  const [ожила, setОжила] = useState(false);

  useEffect(() => {
    if (!ОЖИВЛЁННЫЕ.has(имя)) {
      return;
    }
    // Системное «уменьшить движение» уважается ДО загрузки: человеку, который его включил,
    // незачем скачивать проигрыватель ради картинки, которая двигаться не будет.
    //
    // Вызов через `?.`: спрашивать настройку у среды, которая её не знает, — значит уронить
    // экран целиком. Своей ошибкой это не выглядит: экран пустого состояния просто исчезает,
    // а вместо него встаёт перехватчик. Поймано прогоном проверок 03.09.2026, где `matchMedia`
    // нет вовсе; ровно то же бывает в старых обёртках браузера на телефонах заказчика.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true) {
      return;
    }
    // Под управлением прогона движения нет вовсе.
    //
    // Картинка проигрывается один раз и замирает — значит снимок, сделанный в середине
    // прохода, поймает случайный кадр, и сверка с эталоном начнёт краснеть без единой правки.
    // Тот же признак глушит подсказки первого раза и по той же причине.
    //
    // Створка для проверки глазами: без неё оживлённую картинку нельзя увидеть вообще никак,
    // кроме как открыв окно на живом экране, — а это запрещено (владелец работает за той же
    // машиной). Ключ тот же по смыслу, что у подсказок.
    if (navigator.webdriver && localStorage.getItem(СТВОРКА_ПРОГОНА) !== 'да') {
      return;
    }
    let живо = true;
    let остановить: (() => void) | undefined;

    void (async () => {
      try {
        const [{ default: проигрыватель }, { default: сюжет }] = await Promise.all([
          import('lottie-web/build/player/esm/lottie_light.min.js'),
          import(`./анимации/${имя}.json`),
        ]);
        const место = узел.current;
        if (!живо || место === null) {
          return;
        }
        const разбор = structuredClone(сюжет) as Разбор;
        const линия = разобратьЦвет(window.getComputedStyle(место).color);
        const щуп = щупАкцента.current;
        const акцент = щуп === null ? null : разобратьЦвет(window.getComputedStyle(щуп).color);
        if (линия !== null) {
          перекрасить(разбор, линия, акцент ?? линия);
        }
        const оживление = проигрыватель.loadAnimation({
          container: место,
          renderer: 'svg',
          // Один проход и остановка на последнем кадре: см. пояснение вверху файла.
          loop: false,
          autoplay: true,
          animationData: разбор,
        });
        остановить = () => oживлениеУбрать(оживление);
        setОжила(true);
      } catch {
        // Не приехало — остаётся неподвижная картинка. Пустого места здесь не бывает.
        setОжила(false);
      }
    })();

    return () => {
      живо = false;
      остановить?.();
    };
  }, [имя]);

  return (
    <div className='relative size-32 text-illustration-strong-400' aria-hidden>
      {/* Неподвижная картинка лежит под оживлённой и уходит, когда та поехала: так на месте
          картинки нет ни пустоты, ни подмены одного другим на глазах. */}
      {!ожила && (
        <div className='absolute inset-0 flex items-center justify-center'>
          <Illustration имя={имя} />
        </div>
      )}
      <span ref={щупАкцента} className='hidden text-primary-base' />
      <div ref={узел} className='size-full' />
    </div>
  );
}

/** Убрать проигрыватель. Отдельной функцией, чтобы тип библиотеки не тёк в тело компонента. */
function oживлениеУбрать(оживление: { destroy: () => void }): void {
  оживление.destroy();
}
